import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import ChildBottomNav from "../components/ChildBottomNav";
import EmojiPicker from "emoji-picker-react";
import "./ChildProfile.css";

const DEFAULT_SUGGESTED_ITEM_IMAGE =
  "http://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Prezenty_EXP_096_%28ubt%29.JPG/1280px-Prezenty_EXP_096_%28ubt%29.JPG";

function ChildProfile() {
  const [child, setChild] = useState(null);
  const [editData, setEditData] = useState({
    name: "",
    pin: "",
    photo: "",
  });
  const [status, setStatus] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [showTaskSuggestion, setShowTaskSuggestion] = useState(false);
  const [allChildren, setAllChildren] = useState([]);
  const [otherChildren, setOtherChildren] = useState([]);
  const [transferTo, setTransferTo] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferStatus, setTransferStatus] = useState("");
  const [suggestion, setSuggestion] = useState({
    title: "",
    price: "",
    description: "",
  });
  const [suggestionStatus, setSuggestionStatus] = useState("");
  const [taskSuggestion, setTaskSuggestion] = useState({
    title: "",
    description: "",
    icon: "📝",
    points: "",
    repeat: "once",
    assignedTo: [],
  });
  const [taskSuggestionStatus, setTaskSuggestionStatus] = useState("");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("selectedUser"));
    if (!user || user.type !== "child") {
      window.location.href = "/";
      return;
    }

    if (user.id) {
      setChild(user);
      setEditData({
        name: user.name || "",
        pin: user.pin || "",
        photo: user.photo || "",
      });
      loadChild(user.id);
      loadOtherChildren(user.id);
    }
  }, []);

  const loadChild = async (id) => {
    const ref = doc(db, "users", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;

    const childData = { id: snap.id, ...snap.data() };
    setChild(childData);
    setEditData({
      name: childData.name || "",
      pin: childData.pin || "",
      photo: childData.photo || "",
    });
  };

  const loadOtherChildren = async (currentChildId) => {
    const q = query(collection(db, "users"), where("type", "==", "child"));
    const snap = await getDocs(q);
    const children = snap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "uk"));
    setAllChildren(children);

    const childrenForTransfer = children
      .filter((item) => item.id !== currentChildId)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "uk"));

    setOtherChildren(childrenForTransfer);
    setTransferTo(childrenForTransfer[0]?.id || "");
    setTaskSuggestion((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.length ? prev.assignedTo : [currentChildId],
    }));
  };

  const handleSave = async () => {
    if (!child?.id) return;

    const updated = {
      ...child,
      name: editData.name,
      pin: editData.pin,
      photo: editData.photo,
    };

    await updateDoc(doc(db, "users", child.id), {
      name: editData.name,
      pin: editData.pin,
      photo: editData.photo,
    });

    localStorage.setItem("selectedUser", JSON.stringify(updated));
    setChild(updated);
    setStatus("Профіль збережено");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSize = 300;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = height * (maxSize / width);
            width = maxSize;
          }
        } else if (height > maxSize) {
          width = width * (maxSize / height);
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        setEditData((prev) => ({
          ...prev,
          photo: canvas.toDataURL("image/jpeg", 0.7),
        }));
        setStatus("");
      };
      img.src = event.target.result;
    };

    reader.readAsDataURL(file);
  };

  const resetTransfer = () => {
    setTransferAmount("");
    setTransferStatus("");
    setShowTransfer(false);
  };

  const handleTransfer = async () => {
    if (!child?.id) return;

    const amount = Number(transferAmount);
    if (!transferTo) {
      setTransferStatus("Оберіть, кому переказати бали");
      return;
    }

    if (!Number.isInteger(amount) || amount <= 0) {
      setTransferStatus("Введіть ціле число більше нуля");
      return;
    }

    if (amount >= Number(child.points || 0)) {
      setTransferStatus("Сума має бути меншою за кількість балів на рахунку");
      return;
    }

    try {
      const senderRef = doc(db, "users", child.id);
      const receiverRef = doc(db, "users", transferTo);

      await runTransaction(db, async (transaction) => {
        const senderSnap = await transaction.get(senderRef);
        const receiverSnap = await transaction.get(receiverRef);

        if (!senderSnap.exists() || !receiverSnap.exists()) {
          throw new Error("Користувача не знайдено");
        }

        const senderPoints = Number(senderSnap.data().points || 0);
        const receiverPoints = Number(receiverSnap.data().points || 0);

        if (amount >= senderPoints) {
          throw new Error("Недостатньо балів на рахунку");
        }

        transaction.update(senderRef, { points: senderPoints - amount });
        transaction.update(receiverRef, { points: receiverPoints + amount });
      });

      const updatedChild = {
        ...child,
        points: Number(child.points || 0) - amount,
      };
      setChild(updatedChild);
      localStorage.setItem("selectedUser", JSON.stringify(updatedChild));
      setTransferAmount("");
      setTransferStatus("Бали переказано");
    } catch (error) {
      setTransferStatus(error.message || "Не вдалося переказати бали");
    }
  };

  const resetSuggestion = () => {
    setSuggestion({ title: "", price: "", description: "" });
    setSuggestionStatus("");
    setShowSuggestion(false);
  };

  const handleSuggestionSave = async () => {
    const price = Number(suggestion.price);

    if (!suggestion.title.trim()) {
      setSuggestionStatus("Введіть назву товару");
      return;
    }

    if (!Number.isInteger(price) || price <= 0) {
      setSuggestionStatus("Ціна має бути цілим числом більше нуля");
      return;
    }

    await addDoc(collection(db, "shopItems"), {
      title: suggestion.title.trim(),
      description: suggestion.description.trim(),
      price,
      image: DEFAULT_SUGGESTED_ITEM_IMAGE,
      active: false,
      suggestedBy: child.id,
      suggestedByName: child.name || "",
      createdAt: new Date(),
    });

    setSuggestion({ title: "", price: "", description: "" });
    setSuggestionStatus("Товар запропоновано");
  };

  const resetTaskSuggestion = () => {
    setTaskSuggestion({
      title: "",
      description: "",
      icon: "📝",
      points: "",
      repeat: "once",
      assignedTo: child?.id ? [child.id] : [],
    });
    setTaskSuggestionStatus("");
    setShowTaskSuggestion(false);
  };

  const handleTaskAssignChange = (id) => {
    setTaskSuggestion((prev) => {
      const assignedTo = prev.assignedTo.includes(id)
        ? prev.assignedTo.filter((item) => item !== id)
        : [...prev.assignedTo, id];
      return { ...prev, assignedTo };
    });
    setTaskSuggestionStatus("");
  };

  const handleTaskSuggestionSave = async () => {
    const points = Number(taskSuggestion.points);

    if (!taskSuggestion.title.trim()) {
      setTaskSuggestionStatus("Введіть назву завдання");
      return;
    }

    if (!Number.isInteger(points) || points <= 0) {
      setTaskSuggestionStatus("Бали мають бути цілим числом більше нуля");
      return;
    }

    if (!taskSuggestion.assignedTo.length) {
      setTaskSuggestionStatus("Оберіть, кому призначити завдання");
      return;
    }

    await addDoc(collection(db, "tasks"), {
      title: taskSuggestion.title.trim(),
      description: taskSuggestion.description.trim(),
      icon: taskSuggestion.icon || "📝",
      points,
      repeat: taskSuggestion.repeat,
      assignedTo: taskSuggestion.assignedTo,
      active: false,
      suggestedBy: child.id,
      suggestedByName: child.name || "",
      createdAt: new Date(),
    });

    setTaskSuggestion({
      title: "",
      description: "",
      icon: "📝",
      points: "",
      repeat: "once",
      assignedTo: child?.id ? [child.id] : [],
    });
    setTaskSuggestionStatus("Завдання запропоновано");
  };

  if (!child) {
    return <div className="child-profile-page">Завантаження...</div>;
  }

  return (
    <div className="child-profile-page">
      <div className="child-profile-shell">
        <header className="child-profile-header">
          <img src={editData.photo} alt={editData.name} className="child-profile-avatar" />
          <div>
            <h2>{editData.name}</h2>
            <p>Профіль</p>
          </div>
        </header>

        <main className="child-profile-content">
          <div className="profile-section-buttons">
            <button
              type="button"
              className={showSettings ? "active" : ""}
              onClick={() => {
                setShowSettings((current) => !current);
                setShowTransfer(false);
                setShowSuggestion(false);
                setShowTaskSuggestion(false);
              }}
            >
              Налаштування профілю
            </button>
            <button
              type="button"
              className={showTransfer ? "active" : ""}
              onClick={() => {
                setShowTransfer((current) => !current);
                setShowSettings(false);
                setShowSuggestion(false);
                setShowTaskSuggestion(false);
              }}
            >
              Переказати бали
            </button>
            <button
              type="button"
              className={showSuggestion ? "active" : ""}
              onClick={() => {
                setShowSuggestion((current) => !current);
                setShowSettings(false);
                setShowTransfer(false);
                setShowTaskSuggestion(false);
              }}
            >
              Запропонувати товар
            </button>
            <button
              type="button"
              className={showTaskSuggestion ? "active" : ""}
              onClick={() => {
                setShowTaskSuggestion((current) => !current);
                setShowSettings(false);
                setShowTransfer(false);
                setShowSuggestion(false);
              }}
            >
              Запропонувати завдання
            </button>
          </div>

          {showSettings && (
            <div className="profile-form">
              <label>
                Ім'я
                <input
                  value={editData.name}
                  onChange={(e) => {
                    setEditData({ ...editData, name: e.target.value });
                    setStatus("");
                  }}
                />
              </label>

              <label>
                PIN-код
                <input
                  type="text"
                  value={editData.pin}
                  onChange={(e) => {
                    setEditData({ ...editData, pin: e.target.value });
                    setStatus("");
                  }}
                  inputMode="numeric"
                  maxLength={4}
                />
              </label>

              <label>
                Фото
                <input type="file" accept="image/*" onChange={handleImageUpload} />
              </label>

              {editData.photo && (
                <img src={editData.photo} alt="Попередній перегляд" className="profile-preview" />
              )}

              {status && <p className="profile-status">{status}</p>}

              <div className="profile-actions">
                <button type="button" className="profile-save-btn" onClick={handleSave}>
                  Зберегти
                </button>
                <button
                  type="button"
                  className="profile-cancel-btn"
                  onClick={() => setShowSettings(false)}
                >
                  Скасувати
                </button>
              </div>
            </div>
          )}

          {showTransfer && (
            <div className="profile-form transfer-form">
              <div className="account-points">На рахунку: {Number(child.points || 0)} балів</div>

              <label>
                Кому переказати
                <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)}>
                  {otherChildren.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Кількість балів
                <input
                  type="number"
                  min="1"
                  max={Math.max(0, Number(child.points || 0) - 1)}
                  step="1"
                  value={transferAmount}
                  onChange={(e) => {
                    setTransferAmount(e.target.value);
                    setTransferStatus("");
                  }}
                />
              </label>

              {transferStatus && <p className="profile-status">{transferStatus}</p>}

              <div className="profile-actions">
                <button type="button" className="profile-save-btn" onClick={handleTransfer}>
                  Надіслати
                </button>
                <button type="button" className="profile-cancel-btn" onClick={resetTransfer}>
                  Скасувати
                </button>
              </div>
            </div>
          )}

          {showSuggestion && (
            <div className="profile-form transfer-form">
              <label>
                Назва
                <input
                  value={suggestion.title}
                  onChange={(e) => {
                    setSuggestion({ ...suggestion, title: e.target.value });
                    setSuggestionStatus("");
                  }}
                />
              </label>

              <label>
                Ціна
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={suggestion.price}
                  onChange={(e) => {
                    setSuggestion({ ...suggestion, price: e.target.value });
                    setSuggestionStatus("");
                  }}
                />
              </label>

              <label>
                Опис
                <textarea
                  value={suggestion.description}
                  onChange={(e) => {
                    setSuggestion({ ...suggestion, description: e.target.value });
                    setSuggestionStatus("");
                  }}
                />
              </label>

              {suggestionStatus && <p className="profile-status">{suggestionStatus}</p>}

              <div className="profile-actions">
                <button type="button" className="profile-save-btn" onClick={handleSuggestionSave}>
                  Зберегти
                </button>
                <button type="button" className="profile-cancel-btn" onClick={resetSuggestion}>
                  Скасувати
                </button>
              </div>
            </div>
          )}

          {showTaskSuggestion && (
            <div className="profile-form transfer-form">
              <label>
                Назва
                <input
                  value={taskSuggestion.title}
                  onChange={(e) => {
                    setTaskSuggestion({ ...taskSuggestion, title: e.target.value });
                    setTaskSuggestionStatus("");
                  }}
                />
              </label>

              <label>
                Опис
                <textarea
                  value={taskSuggestion.description}
                  onChange={(e) => {
                    setTaskSuggestion({ ...taskSuggestion, description: e.target.value });
                    setTaskSuggestionStatus("");
                  }}
                />
              </label>

              <div className="profile-icon-preview">{taskSuggestion.icon || "📝"}</div>
              <EmojiPicker
                onEmojiClick={(emojiData) =>
                  setTaskSuggestion({ ...taskSuggestion, icon: emojiData.emoji })
                }
                height={260}
                width="100%"
              />

              <label>
                Бали
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={taskSuggestion.points}
                  onChange={(e) => {
                    setTaskSuggestion({ ...taskSuggestion, points: e.target.value });
                    setTaskSuggestionStatus("");
                  }}
                />
              </label>

              <label>
                Повторення
                <select
                  value={taskSuggestion.repeat}
                  onChange={(e) =>
                    setTaskSuggestion({ ...taskSuggestion, repeat: e.target.value })
                  }
                >
                  <option value="once">Одноразове</option>
                  <option value="daily">Щодня</option>
                  <option value="weekly">Щотижня</option>
                </select>
              </label>

              <div className="profile-check-group">
                <span>Призначено</span>
                {allChildren.map((item) => (
                  <label key={item.id}>
                    <input
                      type="checkbox"
                      checked={taskSuggestion.assignedTo.includes(item.id)}
                      onChange={() => handleTaskAssignChange(item.id)}
                    />
                    {item.name}
                  </label>
                ))}
              </div>

              {taskSuggestionStatus && <p className="profile-status">{taskSuggestionStatus}</p>}

              <div className="profile-actions">
                <button
                  type="button"
                  className="profile-save-btn"
                  onClick={handleTaskSuggestionSave}
                >
                  Запропонувати
                </button>
                <button type="button" className="profile-cancel-btn" onClick={resetTaskSuggestion}>
                  Скасувати
                </button>
              </div>
            </div>
          )}
        </main>

        <ChildBottomNav />
      </div>
    </div>
  );
}

export default ChildProfile;
